import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAllShows } from "../api/client";
import RecoveryPanel from "../components/RecoveryPanel";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import Chip from "../components/ui/Chip";
import EmptyState from "../components/ui/EmptyState";
import { fieldClassName } from "../components/ui/FormField";
import LoadingState from "../components/ui/LoadingState";
import PosterFrame from "../components/ui/PosterFrame";
import ShowCard from "../components/ShowCard";
import {
  COMMITMENT_OPTIONS,
  COMMITMENT_UI_TO_API,
  GENRE_OPTIONS,
  PLATFORM_GROUPS,
  VIBE_OPTIONS,
} from "../constants/recommendationOptions";
import { searchTextForShow, showPath } from "../utils/showDisplay";

const popularSearches = [
  "powerful black women",
  "revenge dramas",
  "feel good movies",
  "black love",
  "heist series",
  "90s classics",
];

const defaultFilters = {
  type: "all",
  genre: "",
  vibe: "",
  commitment: "",
  platform: "",
};

function showIsSeries(show) {
  const media = String(show.media_type || "").toLowerCase();
  const commitment = String(show.commitment_level || "").toLowerCase();
  return media.includes("tv") || media.includes("series") || commitment.includes("series");
}

function showIsMovie(show) {
  const media = String(show.media_type || "").toLowerCase();
  const commitment = String(show.commitment_level || "").toLowerCase();
  return media.includes("movie") || commitment.includes("movie");
}

export default function SearchDiscover() {
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState(defaultFilters);
  const [catalog, setCatalog] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    setStatus("loading");
    setError("");

    fetchAllShows({ limit: 100, sort: "title" })
      .then((response) => {
        if (!active) return;

        const results = response?.data?.results;
        if (!Array.isArray(results)) {
          setCatalog([]);
          setMetadata(null);
          setStatus("error");
          setError("The catalog response was not in the expected shape.");
          return;
        }

        setCatalog(results);
        setMetadata(response?.data?.metadata || null);
        setStatus("success");
      })
      .catch((err) => {
        if (!active) return;

        setCatalog([]);
        setMetadata(null);
        setStatus("error");
        setError(err?.response?.data?.detail || "The catalog could not load.");
      });

    return () => {
      active = false;
    };
  }, []);

  const filteredShows = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return catalog.filter((show) => {
      const searchable = searchTextForShow(show);
      const matchesQuery = !normalizedQuery || searchable.includes(normalizedQuery);
      const matchesType =
        filters.type === "all" ||
        (filters.type === "movies" && showIsMovie(show)) ||
        (filters.type === "series" && showIsSeries(show));
      const matchesGenre =
        !filters.genre ||
        (show.genres || []).some((genre) => genre.toLowerCase() === filters.genre.toLowerCase());
      const matchesVibe =
        !filters.vibe ||
        String(show.laugh_factor || "").toLowerCase() === filters.vibe.toLowerCase() ||
        (show.moods || []).some((mood) => mood.toLowerCase().includes(filters.vibe.toLowerCase()));
      const matchesCommitment =
        !filters.commitment ||
        String(show.commitment_level || "").toLowerCase() === filters.commitment.toLowerCase();
      const matchesPlatform =
        !filters.platform ||
        String(show.platform || "").toLowerCase().includes(filters.platform.toLowerCase());

      return (
        matchesQuery &&
        matchesType &&
        matchesGenre &&
        matchesVibe &&
        matchesCommitment &&
        matchesPlatform
      );
    });
  }, [catalog, filters, query]);

  const topPick = filteredShows[0] || catalog[0] || null;
  const commitmentFilters = COMMITMENT_OPTIONS.slice(0, 3).map(([label, value]) => [
    label,
    COMMITMENT_UI_TO_API[value],
  ]);

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: current[key] === value ? "" : value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setQuery("");
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8 grid gap-6 lg:grid-cols-[280px_1fr]">
        <div>
          <Badge variant="gold" className="mb-5">Search / Discover</Badge>
          <h1 className="font-display text-5xl font-bold leading-tight text-hookd-cream">
            Find your
            <span className="block text-hookd-gold">next obsession.</span>
          </h1>
          <p className="mt-4 text-sm leading-6 text-hookd-muted">
            Search by title, vibe, genre, platform, or keyword.
          </p>
        </div>

        <div>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="block">
              <span className="sr-only">Search catalog</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="powerful black women"
                className={fieldClassName}
              />
            </label>
            <Button type="button" onClick={() => setQuery(query.trim())}>
              Search
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <span className="py-2 text-hookd-muted">Popular searches:</span>
            {popularSearches.map((search) => (
              <Chip
                key={search}
                as="button"
                type="button"
                active={query === search}
                onClick={() => setQuery(search)}
              >
                {search}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <aside>
          <Card className="p-5">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="font-bold text-hookd-cream">Refine Your Search</h2>
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm font-semibold text-hookd-empress"
              >
                Reset all
              </button>
            </div>

            <div className="space-y-6">
              <section>
                <h3 className="mb-3 text-xs font-bold uppercase text-hookd-gold">Type</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    ["All", "all"],
                    ["Movies", "movies"],
                    ["Series", "series"],
                  ].map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={filters.type === value}
                      onClick={() => setFilters((current) => ({ ...current, type: value }))}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase text-hookd-gold">Genres</h3>
                <div className="flex flex-wrap gap-2">
                  {GENRE_OPTIONS.slice(0, 8).map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={filters.genre === value}
                      onClick={() => updateFilter("genre", value)}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase text-hookd-gold">Vibes</h3>
                <div className="flex flex-wrap gap-2">
                  {VIBE_OPTIONS.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={filters.vibe === value}
                      onClick={() => updateFilter("vibe", value)}
                    >
                      {label.split(" — ")[0]}
                    </Chip>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase text-hookd-gold">Commitment</h3>
                <div className="flex flex-wrap gap-2">
                  {commitmentFilters.map(([label, value]) => (
                    <Chip
                      key={value}
                      as="button"
                      type="button"
                      active={filters.commitment === value}
                      onClick={() => updateFilter("commitment", value)}
                    >
                      {label}
                    </Chip>
                  ))}
                </div>
              </section>

              <section>
                <h3 className="mb-3 text-xs font-bold uppercase text-hookd-gold">Platform</h3>
                <div className="flex flex-wrap gap-2">
                  {PLATFORM_GROUPS["🔥 Mainstream"].slice(0, 8).map((platform) => (
                    <Chip
                      key={platform}
                      as="button"
                      type="button"
                      active={filters.platform === platform}
                      onClick={() => updateFilter("platform", platform)}
                    >
                      {platform}
                    </Chip>
                  ))}
                </div>
              </section>
            </div>
          </Card>
        </aside>

        <section className="min-w-0">
          {status === "loading" && (
            <LoadingState title="Loading the catalog" message="Finding the shelves worth browsing." />
          )}

          {status === "error" && (
            <RecoveryPanel
              title="Search did not load"
              message={error}
              variant="error"
              actions={[{ label: "Try Again", onClick: () => window.location.reload() }]}
            />
          )}

          {status === "success" && (
            <>
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-hookd-muted">
                  <span className="font-bold text-hookd-empress">{filteredShows.length}</span>{" "}
                  results{query ? ` for "${query}"` : ""}
                  {metadata?.total_results ? ` from ${metadata.total_results} catalog titles` : ""}
                </p>
                <select
                  value={filters.commitment}
                  onChange={(event) => updateFilter("commitment", event.target.value)}
                  className="rounded-hookd border border-hookd-line bg-hookd-ink px-4 py-3 text-sm text-hookd-cream"
                >
                  <option value="">Sort by: Best Match</option>
                  <option value="Movie">Movies / One-off</option>
                  <option value="Limited Series">Mini Series</option>
                  <option value="Multi-Season">Full Series</option>
                </select>
              </div>

              {topPick && filteredShows.length > 0 && (
                <Card className="mb-6 p-5" glow="purple">
                  <div className="grid gap-5 lg:grid-cols-[190px_1fr_190px] lg:items-center">
                    <PosterFrame show={topPick} className="aspect-[2/3]" />
                    <div>
                      <Badge variant="gold" className="mb-3">Top Pick For Your Vibe</Badge>
                      <h2 className="font-display text-3xl font-bold text-hookd-cream">
                        {topPick.title}
                      </h2>
                      <p className="mt-2 text-sm text-hookd-muted">
                        {topPick.platform || "Platform unknown"} • {topPick.commitment_level || "Format not set"}
                      </p>
                      <p className="mt-4 text-sm leading-6 text-hookd-cream">
                        {(topPick.pitch || {}).hook ||
                          (topPick.pitch || {}).empress_hook ||
                          "A curated pick from the Hook'd catalog."}
                      </p>
                    </div>
                    <div className="grid gap-3">
                      <Button as={Link} to={showPath(topPick)} state={{ show: topPick }}>
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {filteredShows.length === 0 ? (
                <EmptyState
                  title="No matches found"
                  message="Try broadening the search, adjusting filters, or exploring trending picks."
                  primaryAction={{ label: "Explore Trending", to: "/trending" }}
                  secondaryAction={{ label: "Clear Filters", onClick: resetFilters }}
                />
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {filteredShows.map((show) => (
                    <ShowCard
                      key={`${show.id || show.title}`}
                      show={show}
                      detailsTo={showPath(show)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
